import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UtensilsCrossed, Bus, Palmtree, Coffee, Hotel, Compass, Plane, Ticket, CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useDatePickerDefault } from "@/hooks/itinerary-dates";

interface RowData {
  description: { value: string | number };
  quantity: { value: string | number };
  sellPrice: { value: string | number };
}

interface SectionData {
  id: string;
  title: string;
  rows: RowData[];
}

interface ExportToItineraryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  section: SectionData | null;
  sectionTotal: number;
  leadStartDate?: string | null;
  onConfirm: (data: {
    title: string;
    description: string;
    price: number;
    iconId: string;
    date?: string;
  }) => void;
  isLoading?: boolean;
}

const DEFAULT_ICONS = [
  { id: "free-time", label: "Free Time", icon: Palmtree, color: "text-blue-500" },
  { id: "meal", label: "Meal", icon: UtensilsCrossed, color: "text-orange-500" },
  { id: "transfer", label: "Transfer", icon: Bus, color: "text-green-500" },
  { id: "other", label: "Other", icon: Coffee, color: "text-purple-500" },
  { id: "accommodation", label: "Accommodation", icon: Hotel, color: "text-cyan-500" },
  { id: "tours", label: "Tours", icon: Compass, color: "text-emerald-500" },
  { id: "flights", label: "Flights", icon: Plane, color: "text-sky-500" },
  { id: "activities", label: "Activities", icon: Ticket, color: "text-pink-500" },
];

export function ExportToItineraryDialog({
  open,
  onOpenChange,
  section,
  sectionTotal,
  leadStartDate,
  onConfirm,
  isLoading = false,
}: ExportToItineraryDialogProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedIcon, setSelectedIcon] = useState("other");
  const [date, setDate] = useState<Date | undefined>(undefined);
  // Single source of truth for itinerary default date.
  const defaultDate = useDatePickerDefault(leadStartDate);
  // Tracks explicit user date selection; gates the async adoption effect.
  const userHasPicked = useRef(false);

  // Pre-fill form when dialog opens
  useEffect(() => {
    if (open && section) {
      // Set title from section name
      setTitle(section.title);
      
      // Auto-generate description from rows
      const generatedDesc = section.rows
        .filter(row => row.description.value)
        .map(row => {
          const qty = row.quantity.value || 1;
          const desc = row.description.value;
          return `- ${qty}x ${desc} included.`;
        })
        .join("\n");
      
      setDescription(generatedDesc);
      
      // Reset icon to default
      setSelectedIcon("other");

      // Seed from the centralised hook value. Never falls back to today.
      userHasPicked.current = false;
      setDate(defaultDate);
    }
  }, [open, section, defaultDate]);

  // Adopt the hook value when leadStartDate resolves after async load,
  // unless the user has already picked a date.
  useEffect(() => {
    if (!open) return;
    if (userHasPicked.current) return;
    if (!defaultDate) return;
    setDate((prev) => prev ?? defaultDate);
  }, [open, defaultDate]);

  // Reset the "user picked" flag whenever the dialog closes.
  useEffect(() => {
    if (!open) userHasPicked.current = false;
  }, [open]);

  const handleConfirm = () => {
    const payload = {
      title,
      description,
      price: sectionTotal,
      iconId: selectedIcon,
      date: date ? format(date, 'yyyy-MM-dd') : undefined,
    };
    
    console.log('🚀 Export to Itinerary Payload:', payload);
    
    onConfirm(payload);
    
    // Reset form
    setTitle("");
    setDescription("");
    setSelectedIcon("other");
    setDate(undefined);
    userHasPicked.current = false;
  };

  if (!section) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review & Export to Itinerary</DialogTitle>
          <DialogDescription>
            Review and customize the details before adding this item to the itinerary
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Item title"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="date">Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => {
                    userHasPicked.current = true;
                    setDate(d);
                  }}
                  initialFocus
                  defaultMonth={date ?? defaultDate}
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="price">Sell Price (Read-only)</Label>
            <Input
              id="price"
              value={`$${sectionTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              disabled
              className="bg-muted"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Edit the auto-generated description or add your own..."
              rows={8}
            />
            <p className="text-xs text-muted-foreground">
              Tip: You can rewrite this description to add marketing flair or merge items
            </p>
          </div>

          <div className="space-y-3">
            <Label>Select Category Icon *</Label>
            <div className="grid grid-cols-4 gap-3">
              {DEFAULT_ICONS.map((img) => {
                const Icon = img.icon;
                const isSelected = selectedIcon === img.id;
                return (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => setSelectedIcon(img.id)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <Icon className={`h-8 w-8 ${isSelected ? "text-primary" : img.color}`} />
                    <span className="text-xs font-medium">{img.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!title.trim() || !description.trim() || isLoading}
          >
            {isLoading ? "Creating..." : "Create Item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
